import { EventTypes, NodeId, nodesFlux, useEditorSelector } from '@candulabs/craft';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { useEditor } from 'craft/hooks/useEditor';
import { selectCollectionItemEvents } from 'craft/state/collections/selectors';
import { CustomNodeData } from 'craft/types';
import { CssSelectorId } from 'craft/types/cssSelector';

export const useNodeSelectors = (nodeId: NodeId) => {
  const cssSelector = useMemo(
    () =>
      createSelector(
        nodesFlux.selectors.getNodeCustom(nodeId),
        (custom: CustomNodeData) => custom?.css,
      ),
    [nodeId],
  );
  return useEditorSelector(cssSelector);
};

export const useNodeCssSelector = (nodeId: NodeId, cssSelectorId: CssSelectorId) => {
  const cssSelector = useMemo(
    () =>
      createSelector(
        nodesFlux.selectors.getNodeCustom(nodeId),
        (custom: CustomNodeData) => (custom?.cssSelectors ?? {})[cssSelectorId] ?? {},
      ),
    [nodeId],
  );
  return useEditorSelector(cssSelector);
};

export const useNodeDom = (nodeId: NodeId) => {
  const collectionSelectedItemId = useSelector(selectCollectionItemEvents)[EventTypes.selected];
  const selector = useMemo(() => nodesFlux.selectors.getNodeDom(nodeId), [nodeId]);
  const nodeDom = useEditorSelector(selector);
  if (!collectionSelectedItemId) {
    return nodeDom;
  }
  return (
    document.querySelector(
      `[data-nid="${nodeId}-${collectionSelectedItemId}"]`, // targeted element is returned if defined, otherwise from the reducer.
    ) || nodeDom
  );
};

export const useNodeType = (nodeId: NodeId) => {
  const typesSelector = useMemo(() => nodesFlux.selectors.getNodeType(nodeId), [nodeId]);
  return useEditorSelector(typesSelector);
};

export const useNodeCustom = (nodeId: NodeId) => {
  const customSelector = useMemo(() => nodesFlux.selectors.getNodeCustom(nodeId), [nodeId]);
  return useEditorSelector(customSelector);
};

export const useNodeCustomActions = (nodeId: NodeId) => {
  const selector = useMemo(
    () =>
      createSelector(
        nodesFlux.selectors.getNodeCustom(nodeId),
        (custom: CustomNodeData) => custom?.actions,
      ),
    [nodeId],
  );

  return useEditorSelector(selector);
};

export const useNodeDisplayName = (nodeId: NodeId) => {
  const { query } = useEditor();
  const selector = useMemo(
    () =>
      createSelector(
        nodesFlux.selectors.getNodeCustom(nodeId),
        (custom: CustomNodeData) => custom?.displayName,
      ),
    [nodeId],
  );
  const name = useEditorSelector(selector);

  return name || query.node(nodeId)?.displayName();
};
